import { Button, TextLink } from '@edsn/pie-components';
import docIcon from '@iconify/icons-material-symbols/docs';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useUserHasRole } from '#pie/auth/useUserHasRole';
import { Card } from '#pie/components/card/Card';
import { CraftAssetDownload } from '#pie/components/craft-asset-download/CraftAssetDownload';
import { Page, PageHeader } from '#pie/components/page/Page';
import { PageHead } from '#pie/components/page-head/PageHead';
import { Stack } from '#pie/components/stack/Stack';
import { Text } from '#pie/components/text/Text';
import { AuthorizationRoles } from '#pie/internal/api/pie-bff';

export const DataServices = () => {
  const { t } = useTranslation('dataRequest');
  const { t: thl } = useTranslation('hyperlinks');
  const hasSimpleRole = useUserHasRole({
    role: AuthorizationRoles.SimpleDataRequest,
  });

  return (
    <>
      <PageHead title={t('title')} />
      <Page>
        <PageHeader title={t('title')}>
          <div className="flex gap-2">
            <Button
              as={Link}
              to="/datadiensten/verzoeken"
              action="secondary"
              emphasis="bold"
            >
              {t('start.request')}
            </Button>
            {hasSimpleRole && (
              <Button
                as={Link}
                to="/datadiensten/verzoeken/eenvoudig"
                action="secondary"
                emphasis="bold"
              >
                {t('start.simple_request')}
              </Button>
            )}
          </div>
        </PageHeader>
        <Stack gap="lg" className="mx-auto my-6 max-w-4xl">
          <Card
            size="md"
            heading={
              <Text as="h2" variant="h5">
                {t('overview.title')}
              </Text>
            }
          >
            <Stack gap="lg">
              <Text>{t('overview.text.paragraph_one')}</Text>
              <Stack gap="sm">
                <Text as="h2" variant="h6">
                  {t('overview.subtitle_one')}
                </Text>
                <Text>{t('overview.text.paragraph_two')}</Text>
                <TextLink
                  className="w-fit"
                  href={thl('liander.open_data')}
                  target="_blank"
                >
                  {t('data_request_receiver.Liander')}
                </TextLink>

                <TextLink
                  className="w-fit"
                  href={thl('stedin.open_data')}
                  target="_blank"
                >
                  {t('data_request_receiver.Stedin')}
                </TextLink>
                <TextLink
                  className="w-fit"
                  href={thl('rendo.open_data')}
                  target="_blank"
                >
                  {t('data_request_receiver.Rendo')}
                </TextLink>
                <TextLink
                  className="w-fit"
                  href={thl('coteq.open_data')}
                  target="_blank"
                >
                  {t('data_request_receiver.Coteq')}
                </TextLink>
                <TextLink
                  className="w-fit"
                  href={thl('westlandinfra.open_data')}
                  target="_blank"
                >
                  {t('data_request_receiver.WestlandInfra')}
                </TextLink>
                <TextLink
                  className="w-fit"
                  href={thl('enexis.open_data')}
                  target="_blank"
                >
                  {t('data_request_receiver.Enexis')}
                </TextLink>
              </Stack>
              <Stack gap="sm">
                <Text variant="h6">{t('overview.subtitle_two')}</Text>
                <Text>
                  {t('overview.text.paragraph_three')}{' '}
                  <a
                    href={thl('kadaster.klic')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-action-primary-enabled underline"
                  >
                    {t('overview.text.paragraph_three_link')}
                  </a>
                </Text>
              </Stack>

              <Stack gap="sm">
                <Text variant="h6">{t('overview.subtitle_three')}</Text>
                <Stack as="ol" gap="sm" className="ml-6 list-decimal">
                  <li className="text-content-heading font-bold">
                    <Text className="text-content-heading font-bold">
                      {t('overview.subtitle_three_step_one')}
                    </Text>
                    <Text className="text-content-body">
                      {t('overview.text.paragraph_three_step_one_part_one')}{' '}
                      <a
                        href={thl('government_data')}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-action-primary-enabled underline"
                      >
                        {t('overview.text.paragraph_three_step_one_link')}
                      </a>{' '}
                      {t('overview.text.paragraph_three_step_one_part_two')}
                    </Text>
                  </li>
                  <li className="text-content-heading font-bold">
                    <Text className="text-content-heading font-bold">
                      {t('overview.subtitle_three_step_two')}
                    </Text>
                    <Stack gap="sm">
                      <Text className="text-content-body">
                        {t('overview.text.paragraph_three_step_two_part_one')}{' '}
                        <a
                          href="mailto:servicedesk@edsn.nl"
                          className="text-action-primary-enabled underline"
                        >
                          {t('overview.text.paragraph_three_step_two_link')}
                        </a>
                        {t('overview.text.paragraph_three_step_two_part_two')}
                      </Text>
                    </Stack>
                  </li>
                  <li className="text-content-heading font-bold">
                    <Text className="text-content-heading font-bold">
                      {t('overview.subtitle_three_step_three')}
                    </Text>
                    <Stack gap="sm">
                      <Text className="text-content-body">
                        {t('overview.text.paragraph_three_step_three_part_one')}
                      </Text>
                      <ul>
                        <li>
                          {t(
                            'overview.text.paragraph_three_step_three_step_one',
                          )}
                        </li>
                        <li>
                          {t(
                            'overview.text.paragraph_three_step_three_step_two',
                          )}
                        </li>
                        <li>
                          {t(
                            'overview.text.paragraph_three_step_three_step_three',
                          )}
                        </li>
                      </ul>
                      <Text className="text-content-body">
                        {t('overview.text.paragraph_three_step_three_part_two')}
                      </Text>
                      <Text className="text-content-body">
                        {t(
                          'overview.text.paragraph_three_step_three_part_three',
                        )}
                      </Text>
                    </Stack>
                  </li>
                </Stack>
              </Stack>

              <Stack gap="sm">
                <Text variant="h6">{t('overview.subtitle_five')}</Text>
                <Text>{t('overview.text.paragraph_five_part_one')}</Text>
                <ul className="ml-6 list-disc">
                  <li> {t('overview.text.paragraph_five_part_three_one')}</li>
                  <li> {t('overview.text.paragraph_five_part_three_two')}</li>
                  <li> {t('overview.text.paragraph_five_part_three_three')}</li>
                  <li> {t('overview.text.paragraph_five_part_three_four')}</li>
                  <li> {t('overview.text.paragraph_five_part_three_five')}</li>
                </ul>
                <Text>
                  {t('overview.text.paragraph_five_part_four')}{' '}
                  <a
                    href="mailto:lianderdatadiensten@partnersinenergie.nl"
                    className="text-action-primary-enabled underline"
                  >
                    {t('overview.text.paragraph_five_part_four.link')}
                  </a>
                </Text>
              </Stack>

              <Stack gap="sm">
                <Text variant="h6">{t('overview.subtitle_six')}</Text>
                <Text>
                  {t('overview.text.paragraph_six_part_one')}{' '}
                  <a
                    href={thl('privacy')}
                    className="text-action-primary-enabled underline"
                    target="_blank noreferrer noopener"
                  >
                    {t('overview.text.paragraph_six_link')}
                  </a>{' '}
                  {t('overview.text.paragraph_six_part_two')}
                </Text>
              </Stack>
              <Stack gap="sm">
                <Text variant="h6">{t('overview.subtitle_seven')}</Text>
                <Text>{t('overview.text.paragraph_seven')}</Text>
              </Stack>
              <CraftAssetDownload
                iconStart={docIcon}
                title="Voorbeeld toestemmingsformulier klantmandaat"
              />
              <CraftAssetDownload
                iconStart={docIcon}
                title="Aanleveren clusters voorbeeldbestand"
              >
                {t('overview.download_cluster_example')}
              </CraftAssetDownload>
              <Stack className="flex gap-4">
                <Button
                  action="secondary"
                  emphasis="bold"
                  as={Link}
                  to="/datadiensten/verzoeken"
                >
                  {t('start.request')}
                </Button>
                {hasSimpleRole && (
                  <Button
                    as={Link}
                    to="/datadiensten/verzoeken/eenvoudig"
                    action="secondary"
                    emphasis="bold"
                  >
                    {t('start.simple_request')}
                  </Button>
                )}
              </Stack>
            </Stack>
          </Card>
        </Stack>
      </Page>
    </>
  );
};
